import { Carousel } from 'antd';
import { includes } from 'lodash';
import { useSelector } from 'react-redux';
import React, { ReactNode, useRef, useState } from 'react';
import { carouselProperties } from './config/index';
import {
  CarouselCounter,
  CarouselWrapper,
  Description,
} from './MobileCarousel.style';
import { trackArticleArrow } from '../../common/analytics/analyticsTracker';
import { getArticleTypeInGA } from '../../common/analytics/helpers/articleTypeHelper';
import { WholeState } from '../../store/common/type';
import { PostSubcategory } from '../../common/helpers/domain/postCategories';
import { VideoType } from '../../common/helpers/domain/videoTypes';

interface PostingData {
  postCategory?: PostSubcategory;
  videoType?: VideoType;
}
interface MobileCarouselProps {
  children: ReactNode;
  totalItem: number;
  postingData: PostingData;
  source: string;
  captions: string[];
}
const MobileCarousel: React.FC<MobileCarouselProps> = ({
  source,
  postingData,
  totalItem,
  captions,
  children,
}) => {
  const [carouselStart, setCarouselStart] = useState(
    carouselProperties.indexStart,
  );
  const [carouselEnd, setCarouselEnd] = useState(carouselProperties.indexEnd);
  const [activeSlide, setActiveSlide] = useState(1);

  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  const articleTypeInGA = getArticleTypeInGA(
    postingData.postCategory,
    postingData.videoType,
  );

  /**
   * @type {React.MutableRefObject<Carousel>}
   */
  const mobileCarouselRef = useRef(null);

  const settings = {
    dots: true,
    arrows: true,
    speed: 400,
    swipeToSlide: false,
    infinite: false,
    adaptiveHeight: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: false,
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      mobileCarouselRef.current &&
      (includes(target.className, 'slick-arrow slick-next') ||
        includes(target.className, 'slick-arrow slick-prev'))
    ) {
      trackArticleArrow(userInfo, { ...postingData, articleTypeInGA }, source);
    }
  };

  return (
    <CarouselWrapper
      data-test={'mobile-carousel'}
      start={carouselStart}
      end={carouselEnd}
      onClick={handleClick}
    >
      <Carousel
        ref={mobileCarouselRef}
        className="Carousel"
        {...settings}
        onInit={() => {
          if (totalItem < carouselProperties.indexEnd) {
            setTimeout(() => {
              setCarouselEnd(totalItem);
            }, carouselProperties.timeOut);
          }
        }}
        beforeChange={(oldIndex, newIndex) => {
          let start = carouselProperties.indexStart;
          let end = carouselProperties.indexEnd;
          const currentIndex = newIndex + 1;
          setActiveSlide(currentIndex);

          if (currentIndex < carouselProperties.startMinIndex) {
            start = carouselProperties.indexStart;
            end = carouselProperties.indexEnd;
            if (totalItem < carouselProperties.indexEnd) {
              end = totalItem;
            }
            setTimeout(() => {
              setCarouselStart(start);
              setCarouselEnd(end);
            }, carouselProperties.timeOut);
          } else {
            const lastMove = totalItem - carouselProperties.indexMovement;
            start = currentIndex - carouselProperties.indexMovement;
            end = currentIndex + carouselProperties.indexMovement;
            if (currentIndex > lastMove) {
              start =
                lastMove - carouselProperties.indexMovement > 0
                  ? lastMove - carouselProperties.indexMovement
                  : 1;
              end = totalItem;
            }
            setTimeout(() => {
              setCarouselStart(start);
              setCarouselEnd(end);
            }, carouselProperties.timeOut);
          }
        }}
      >
        {children}
      </Carousel>
      <CarouselCounter>
        Image {activeSlide}/{totalItem}
      </CarouselCounter>
      <Description>{captions[activeSlide - 1]}</Description>
    </CarouselWrapper>
  );
};

export default MobileCarousel;
